import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import { connectCliniko, disconnectCliniko, getClinikoStatus } from "../../api/cliniko";

const ClinikoSettings = () => {
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [baseUrl, setBaseUrl] = useState<string>("");
  const [clinikoBusinessId, setClinikoBusinessId] = useState<string>("");
  const [clinikoTimezone, setClinikoTimezone] = useState<string>("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [apiKey, setApiKey] = useState<string>("");

  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getClinikoStatus();
      setConnected(!!data?.connected);
      setBaseUrl(data?.base_url || "");
      setClinikoBusinessId(data?.cliniko_business_id || "");
      setClinikoTimezone(data?.cliniko_timezone || "");
      setUpdatedAt(data?.updated_at || null);
    } catch (err: any) {
      console.error("❌ Error fetching Cliniko status:", err);
      setError("Failed to fetch Cliniko status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  const openDialog = () => {
    setApiKey("");
    setError(null);
    setDialogOpen(true);
  };

  const saveConnection = async () => {
    setLoading(true);
    setError(null);
    try {
      await connectCliniko({
        base_url: baseUrl,
        api_key: apiKey,
      });
      setDialogOpen(false);
      setMessage("Cliniko connected successfully");
      await refreshStatus();
    } catch (err: any) {
      console.error("❌ Cliniko connect error:", err);
      const apiMsg = err?.response?.data?.error;
      setError(apiMsg || err?.message || "Failed to connect Cliniko.");
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async () => {
    setLoading(true);
    setError(null);
    try {
      await disconnectCliniko();
      setMessage("Cliniko disconnected");
      await refreshStatus();
    } catch (err: any) {
      console.error("❌ Cliniko disconnect error:", err);
      setError(err?.response?.data?.error || err?.message || "Failed to disconnect Cliniko.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Cliniko Integration
      </Typography>

      {connected ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Connected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Base URL: <strong>{baseUrl}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Business ID: <strong>{clinikoBusinessId}</strong>
          </Typography>
          {clinikoTimezone && (
            <Typography variant="body2" color="text.secondary">
              Timezone: <strong>{clinikoTimezone}</strong>
            </Typography>
          )}
          {updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Updated: {updatedAt}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect your Cliniko account to use Cliniko appointment types, practitioners, and availability.
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          variant="contained"
          onClick={openDialog}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {connected ? "Update Cliniko" : "Connect Cliniko"}
        </Button>

        {connected && (
          <Button variant="outlined" color="error" onClick={disconnect} disabled={loading}>
            Disconnect
          </Button>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{connected ? "Update Cliniko connection" : "Connect Cliniko"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="dense"
            label="Cliniko base URL"
            placeholder="https://api.au1.cliniko.com/v1"
            fullWidth
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Cliniko API key"
            type="password"
            fullWidth
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveConnection} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </>
  );
};

export default ClinikoSettings;
